/**
 * TechnologyAssetsTable Component
 *
 * Enhanced table for displaying assets impacted by a technology with:
 * - Status column (first position, matches vulnerability drawer pattern)
 * - Asset column (combines IP + Domain for readability)
 * - Port and Service columns (critical for technology context)
 * - Filter dropdowns (status, attack surface, protocol, port)
 * - Search bar
 * - Bulk selection and actions
 *
 * This component uses the specialized useQueryTechnologyAssets hook for
 * filter management and maintains port-specific asset relationships.
 */

import React, { useCallback, useMemo, useState } from 'react';

import { DashedTag } from '@/components/chariot-ui';
import { SoundRecognitionSearch as SoundRecognitionSearchIcon } from '@/components/chariot-ui';
import { createAssetExportConfig } from '@/components/export/configs/asset.export.config';
import { UniversalExportModal } from '@/components/export/universal';
import { SearchBar } from '@/components/legacy-ui/SearchBar';
import {
  createAllEmptyConfig,
  createFilteredEmptyConfig,
  NO_DATA_ICON_CLASS,
} from '@/components/legacy-ui/utils/createNoDataConfig';
import { NoteModal } from '@/components/modals';
import {
  buildAnnotateAction,
  buildExportAction,
  TableActionsBar,
} from '@/components/table/actions';
import { BulkConfirmationDialog } from '@/components/table/BulkConfirmationDialog';
import {
  getColumnWidth,
  isColumnSortable,
} from '@/components/table/columns/registry';
import { Table } from '@/components/table/Table';
import { TableControlsContainer } from '@/components/table/TableControlsContainer';
import { TableSelectionBar } from '@/components/table/TableSelectionBar';
import { Skeleton } from '@/components/ui/skeleton';
import { Filters } from '@/hooks/useAssets';
import { shouldUseBulk } from '@/hooks/useBulkAction/useBulkAction';
import { useBulkConfirmation } from '@/hooks/useBulkConfirmation/useBulkConfirmation';
import type { BulkActionPayload } from '@/hooks/useBulkSelection/types';
import { useAddComment } from '@/hooks/useComment';
import { useHasActiveFilters } from '@/hooks/useHasActiveFilters';
import { queryClient } from '@/queryclient';
import { AssetTableFilters } from '@/sections/asset/components/AssetTableFilters';
import { AssetStatusComponent } from '@/sections/asset/components/badges';
import { AssetRemoveTag as RemoveTagModal } from '@/sections/asset/components/modals/RemoveTagModal';
import { AssetTag as TagModal } from '@/sections/asset/components/modals/TagModal';
import { TechnologyAssetCell } from '@/sections/technology/components/cells';
import {
  ImpactedAssetPort,
  useQueryTechnologyAssets,
} from '@/sections/technology/hooks/drawer';
import { useGlobalState } from '@/state/global.state';
import { useOsintContext } from '@/state/OsintContext';

/**
 * Props for TechnologyAssetsTable component
 */
export interface TechnologyAssetsTableProps {
  /** Technology key to filter assets by */
  technologyKey: string;
  /** Limit visible filters in drawer context */
  drawerFilters?: string[];
}

/**
 * Visible filters for the technology assets table
 * These filters are shown using the shared AssetTableFilters component
 */
const VISIBLE_FILTERS = ['status', 'attackSurface', 'protocol', 'port'];

/**
 * TechnologyAssetsTable Component
 *
 * Displays assets impacted by a technology with filtering, search, and bulk actions.
 * Preserves Port and Service columns which are critical for showing which
 * port/service the technology runs on.
 */
export function TechnologyAssetsTable({
  technologyKey,
  drawerFilters = ['status', 'attackSurface', 'protocol', 'port'],
}: TechnologyAssetsTableProps) {
  const { drawerState } = useGlobalState();
  const { isOsint } = useOsintContext();

  // Data fetching with filter management
  const {
    data: impactedAssetPorts,
    filters,
    setFilters,
    resetFilters,
    status,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    invalidate,
  } = useQueryTechnologyAssets({
    technologyKey,
    enabled: Boolean(technologyKey),
  });

  // Selection state
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  // Compute selected asset-ports from keys
  const selectedAssetPorts = useMemo(
    () =>
      impactedAssetPorts.filter(item => selectedKeys.includes(item.asset.key)),
    [impactedAssetPorts, selectedKeys]
  );

  // Extract assets from selected asset-ports (for modals that expect Asset[])
  const selectedAssets = useMemo(
    () => selectedAssetPorts.map(item => item.asset),
    [selectedAssetPorts]
  );

  // Modal state
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isRemoveTagModalOpen, setIsRemoveTagModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  // Create export config (no source options needed for drawer context)
  const assetExportConfig = useMemo(() => createAssetExportConfig([]), []);

  // Get visible filters - use drawerFilters if provided, otherwise default
  const visibleFilters = useMemo(
    () =>
      drawerFilters.filter(f => VISIBLE_FILTERS.includes(f)) as Array<
        keyof NonNullable<Filters['advancedFilters']>
      >,
    [drawerFilters]
  );

  // Detect if any filters are active (for empty state differentiation)
  const hasActiveFilters = useHasActiveFilters(filters);

  // Clear all filters callback
  const handleClearFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedKeys([]);
  }, []);

  // Build local selectionState from selectedKeys.
  // INVARIANT: assetKeys passed to RemoveTagModal is always selectedKeys (same source).
  const selectionState = useMemo(
    () => ({ mode: 'manual' as const, keys: selectedKeys }),
    [selectedKeys]
  );

  // Minimal bulkSelection adapter for useBulkConfirmation.
  // The drawer table uses raw selectedKeys (not useBulkSelection), so we provide
  // a lightweight adapter that satisfies the toBulkPayload contract.
  const drawerBulkSelection = useMemo(
    () => ({
      toBulkPayload: (): BulkActionPayload['selection'] => ({
        mode: 'explicit' as const,
        keys: selectedKeys,
      }),
    }),
    [selectedKeys]
  );

  // Bulk confirmation for untag. selectedKeys can reach hundreds in the drawer
  // table (all assets for a technology), so bulk path is warranted.
  const untagConfirm = useBulkConfirmation({
    resource: 'asset',
    action: 'untag',
    bulkSelection: drawerBulkSelection,
    clearSelection,
  });

  // Add comment mutation
  const { mutateAsync: addComment } = useAddComment();

  // Handle note submission
  const handleNoteSubmit = useCallback(
    async (note: string) => {
      const promises = selectedAssets.map(asset =>
        addComment({
          key: asset.key,
          name: asset.identifier || asset.key,
          comment: note,
          showSnackbar: false,
        })
      );

      await Promise.all(promises);

      // Invalidate queries
      queryClient.invalidateQueries({
        predicate: query => {
          const queryKey = query.queryKey as string[];
          return queryKey[0] === 'MY' && queryKey[1] === 'asset';
        },
        refetchType: 'active',
      });

      setIsNoteModalOpen(false);
      clearSelection();
    },
    [selectedAssets, addComment, clearSelection]
  );

  // Handle search change
  const handleSearchChange = useCallback(
    (value: string) => {
      setFilters(prev => ({
        ...prev,
        search: value,
      }));
    },
    [setFilters]
  );

  // Handle tag modal success
  const handleTagSuccess = useCallback(() => {
    invalidate();
    clearSelection();
  }, [invalidate, clearSelection]);

  // Handle row click - open asset drawer (disabled in OSINT mode)
  const handleRowClickFn = useCallback(
    ({ asset }: ImpactedAssetPort) => {
      drawerState.handleOpenAssetDrawer(asset);
    },
    [drawerState]
  );
  const handleRowClick = isOsint ? undefined : handleRowClickFn;

  // Dynamic noData config based on filter state
  const noDataConfig = useMemo(() => {
    const icon = (
      <SoundRecognitionSearchIcon
        className={`${NO_DATA_ICON_CLASS} text-[100px]`}
      />
    );

    // If filters are active and no results, show filtered empty state
    if (hasActiveFilters && impactedAssetPorts.length === 0) {
      return createFilteredEmptyConfig({
        entityName: 'assets',
        icon,
        hasActiveFilters,
        onClearFilters: handleClearFilters,
      });
    }

    // Otherwise show the default empty state
    return createAllEmptyConfig({
      entityName: 'No assets impacted by this technology',
      icon,
    });
  }, [hasActiveFilters, impactedAssetPorts.length, handleClearFilters]);

  return (
    <div className="flex h-full flex-col">
      <TableControlsContainer
        data-testid="technology-assets-controls"
        filters={
          // Note: ClearFiltersButton is already rendered inside AssetTableFilters
          <AssetTableFilters
            filters={filters as Filters}
            setFilters={
              setFilters as React.Dispatch<React.SetStateAction<Filters>>
            }
            visibleFilters={visibleFilters}
          />
        }
        filtersRight={
          <SearchBar
            size="sm"
            className="h-fit w-64"
            placeholder="Search assets..."
            value={filters.search ?? ''}
            onChange={handleSearchChange}
          />
        }
        selectionBar={
          <TableSelectionBar
            embedded={true}
            alwaysVisible={true}
            isVisible={selectedKeys.length > 0}
            selectedCount={selectedKeys.length}
            totalCount={impactedAssetPorts.length}
            hasNextPage={hasNextPage}
            resourceLabel="assets"
            emptySelectionMessage="Select assets to perform bulk actions"
            actions={
              <TableActionsBar
                selectedItems={selectedAssets}
                actions={[
                  buildAnnotateAction({
                    onCommentClick: () => setIsNoteModalOpen(true),
                    onTagClick: () => setIsTagModalOpen(true),
                    onRemoveTagClick: () => setIsRemoveTagModalOpen(true),
                    labels: { comment: 'Add Note' },
                    testId: 'technology-assets-annotate-dropdown',
                  }),
                  buildExportAction({
                    onExportClick: () => setIsExportModalOpen(true),
                    disabled: false,
                    testId: 'technology-assets-export-button',
                  }),
                ]}
                disabled={selectedKeys.length === 0}
                hideLabel={true}
                testIdPrefix="technology-assets"
              />
            }
          />
        }
      />

      <div className="min-h-0 flex-1">
        {status === 'pending' ? (
          <Skeleton className="m-6 w-[calc(100%-48px)]" />
        ) : (
          <Table<ImpactedAssetPort>
            name="technologyImpactedAssets"
            error={error}
            status={status}
            data={impactedAssetPorts}
            selection={{
              value: selectedKeys,
              onChange: setSelectedKeys,
            }}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetching={isFetching}
            isFetchingNextPage={isFetchingNextPage}
            onRowClick={handleRowClick}
            columns={[
              {
                id: '',
                label: 'Status',
                copy: false,
                cell: ({ asset }) => <AssetStatusComponent asset={asset} />,
                ...getColumnWidth('shared', 'status'),
                sorting: isColumnSortable('shared', 'status'),
              },
              {
                id: '',
                label: 'Asset',
                copy: true,
                cell: ({ asset }) => <TechnologyAssetCell asset={asset} />,
                ...getColumnWidth('asset', 'identifier'),
                sorting: isColumnSortable('asset', 'identifier'),
              },
              {
                id: '',
                label: 'Port',
                copy: true,
                cell: data => data.port.port.toString(),
                ...getColumnWidth('shared', 'port'),
                sorting: isColumnSortable('shared', 'port'),
              },
              {
                id: '',
                label: 'Service',
                copy: true,
                cell: data => (
                  <DashedTag
                    className="max-w-full justify-normal space-x-0 truncate overflow-hidden text-center [&>span]:w-full"
                    label={data.port.service || '-'}
                    size="xs"
                  />
                ),
                ...getColumnWidth('shared', 'service'),
                sorting: isColumnSortable('shared', 'service'),
              },
              {
                id: 'visited',
                label: 'Last Seen',
                copy: true,
                cell: 'date',
                ...getColumnWidth('shared', 'visited'),
                sorting: isColumnSortable('shared', 'visited'),
              },
            ]}
            noData={noDataConfig}
          />
        )}
      </div>

      {/* Modals */}
      <NoteModal
        open={isNoteModalOpen}
        onClose={() => setIsNoteModalOpen(false)}
        onSubmit={handleNoteSubmit}
        resourceType="Asset"
        mode="simple"
        selectedCount={selectedKeys.length}
        presentation="modal"
      />

      <TagModal
        assetKeys={selectedKeys}
        currentTags={[]}
        open={isTagModalOpen}
        onClose={() => setIsTagModalOpen(false)}
        onSuccess={handleTagSuccess}
      />

      <RemoveTagModal
        assetKeys={selectedKeys}
        open={isRemoveTagModalOpen}
        onClose={() => setIsRemoveTagModalOpen(false)}
        onSuccess={handleTagSuccess}
        selectionState={
          shouldUseBulk(selectionState) ? selectionState : undefined
        }
        onBulkRequested={tag =>
          untagConfirm.openConfirmation({
            action: 'untag',
            actionName: 'untag',
            params: { tag },
          })
        }
      />
      <BulkConfirmationDialog
        isOpen={untagConfirm.confirmPayload !== null}
        actionName={untagConfirm.confirmPayload?.actionName ?? ''}
        resourceLabel="assets"
        isSelectAll={false}
        itemCount={selectedKeys.length}
        isPending={untagConfirm.isPending}
        onCancel={untagConfirm.closeConfirmation}
        onConfirm={untagConfirm.submitConfirmed}
      />

      <UniversalExportModal
        config={assetExportConfig}
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        selectedRows={selectedKeys}
        onClearSelection={() => {
          clearSelection();
          setIsExportModalOpen(false);
        }}
      />
    </div>
  );
}
